import {
  FlexBox,
  SanitizedTrimmedHtml,
  ThumbnailLayout,
} from '@airship-platform/jam-web/components';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { PropsWithChildren } from 'react';
import { Link } from 'react-router';

type ProductDetail = {
  images: { url?: string; type: string }[];
  name: string;
  code?: string;
  model?: string;
  description: string;
};

type ProductCardProps = {
  product: ProductDetail;
  priceSection: React.ReactNode;
  noImageIcon?: React.ReactNode;
  productNameSuffix?: string | React.ReactNode;
  showDescription?: boolean;
  productClickRoute?: string;
};

const ProductCard: React.FC<PropsWithChildren<ProductCardProps>> = ({
  product,
  noImageIcon,
  productNameSuffix,
  priceSection,
  showDescription,
  productClickRoute,
  children,
}) => (
  <FlexBox
    flexDirection={['column', 'row']}
    alignItems={'center'}
    justifyContent={'space-between'}
    gap={3}
    flex={1}
  >
    <ThumbnailLayout
      flexDirection={['column', 'row']}
      flex={1}
      width={['100%', 'auto']}
      imageUrl={product.images
        .filter((i) => i.type === 'Image')
        .map((i) => i.url)
        .find(Boolean)}
      imageAlt={product.name}
      imageProps={{ to: productClickRoute }}
      fallback={noImageIcon}
    >
      <FlexBox flexDirection={'column'} flex={1} gap={[1, 0]}>
        <Box
          component={productClickRoute ? Link : 'div'}
          {...(productClickRoute && { to: productClickRoute })}
          sx={{
            textDecoration: 'none',
            color: 'inherit',
          }}
        >
          <Typography
            variant="h4"
            gutterBottom={false}
            component={'div'}
            sx={{
              gap: [0.5, 2],
              cursor: productClickRoute ? 'pointer' : 'default',
              userSelect: 'none',
            }}
          >
            {product.name} {productNameSuffix}
          </Typography>
        </Box>
        <FlexBox
          flexDirection={['column', 'row']}
          gap={1.5}
          alignItems={'center'}
          justifyContent={'space-between'}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
              width: '100%',
              flex: 1,
            }}
          >
            <Box
              component={productClickRoute ? Link : 'div'}
              {...(productClickRoute && { to: productClickRoute })}
              sx={{
                textDecoration: 'none',
                color: 'inherit',
                ...(productClickRoute && {
                  cursor: 'pointer',
                  userSelect: 'none',
                }),
              }}
            >
              <FlexBox justifyContent={'space-between'} alignItems={'center'}>
                <FlexBox flexDirection={'column'} gap={1}>
                  <Typography variant="body2">
                    {product.code ? 'Code: ' + product.code : ''}
                  </Typography>
                  <Typography variant="body2">
                    {product.model ? 'Model: ' + product.model : ''}
                  </Typography>
                  {(showDescription ?? true) && (
                    <FlexBox flexDirection={'column'} gap={1}>
                      <SanitizedTrimmedHtml
                        html={product.description}
                        variant="body2"
                        maxLength={200}
                      />
                      {product.description.length > 200 && (
                        <Typography
                          color="tertiary"
                          variant="subtitle2"
                          noWrap
                          sx={{
                            textDecoration: 'underline',
                            alignSelf: 'flex-start',
                          }}
                        >
                          Learn more
                        </Typography>
                      )}
                    </FlexBox>
                  )}
                </FlexBox>
              </FlexBox>
            </Box>
            {priceSection}
          </Box>
          {children}
        </FlexBox>
      </FlexBox>
    </ThumbnailLayout>
  </FlexBox>
);

export default ProductCard;
